<template>
  <div style="padding: 20px; min-height: 630px; background-color: rgba(221, 225, 230, 1); border: solid 1px #ccc">
    <div class="metric-filters">
      <span class="title_bi metric-filters-title">Estado Económico</span>
      <div class="metric-filter metric-filter-empresa">
        <v-autocomplete v-model="emitters_selected" :items="emitters" item-text="name" item-value="id" label="Empresas" multiple small-chips dense hide-details autocomplete="off" class="pt-0 mt-0" @change="onEmittersChange">
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 2" small class="ma-0 mr-1">{{ item.name }}</v-chip>
            <span v-if="index === 2" class="grey--text text-caption">(+{{ emitters_selected.length - 2 }})</span>
          </template>
        </v-autocomplete>
      </div>
      <v-btn-toggle v-model="period_type" mandatory dense class="metric-filter-toggle" @change="load">
        <v-btn small value="month">Mes</v-btn>
        <v-btn small value="year">Año</v-btn>
      </v-btn-toggle>
      <div v-if="period_type === 'month'" class="metric-filter metric-filter-mes">
        <v-combobox v-model="mes_selected" autocomplete="off" item-text="name" item-value="id" :items="months" label="Mes" class="pt-0 mt-0" dense hide-details @change="load"></v-combobox>
      </div>
      <div class="metric-filter metric-filter-anio">
        <v-combobox v-model="year" :items="years" label="Año" class="pt-0 mt-0" dense hide-details @change="load"></v-combobox>
      </div>
    </div>

    <p v-if="emitters_selected.length > 1" class="caption grey--text mt-n3 mb-3">
      Consolidado de {{ emitters_selected.length }} empresas. Se excluyen las transferencias internas; las operaciones entre empresas propias no se eliminan.
    </p>
    <p v-if="emitters.length === 0 && !emitters_error && !loading" class="caption mt-4">No hay empresas emisoras configuradas.</p>

    <!-- La grilla con titulos se ve siempre; mientras carga, cada valor muestra su skeleton. -->
    <template v-if="loading || data">
      <!-- Indicadores -->
      <div class="row">
        <div class="col-md-3" v-for="kpi in kpis" :key="kpi.title">
          <v-card class="metric-card metric-kpi-card">
            <p class="title_bi mb-0">{{ kpi.title }}</p>
            <p class="caption mb-0">{{ kpi.caption }}</p>
            <div class="metric-kpi">
              <p v-if="ready" class="text-h6 font-weight-bold mb-1" :style="kpi.color ? 'color:' + kpi.color : ''">{{ kpi.value }}</p>
              <v-skeleton-loader v-else type="heading" width="170" class="metric-skeleton-value mb-2"></v-skeleton-loader>
              <span class="caption">&nbsp;</span>
            </div>
          </v-card>
        </div>
      </div>

      <!-- Ventas vs Gastos -->
      <div class="row">
        <div class="col-md-8">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">Ventas vs Gastos</p>
              <p class="caption text-slate-500 mb-0">Devengado, neto sin IVA. Los gastos suman comprobantes y egresos directos por tesorería</p>
            </div>
            <div class="metric-chips">
              <template v-if="ready">
                <v-chip small outlined :color="COLOR_VENTAS">Ventas: {{ money(k.ventas) }}</v-chip>
                <v-chip small outlined :color="COLOR_GASTOS">Gastos: {{ money(k.gastos) }}</v-chip>
              </template>
              <template v-else>
                <v-skeleton-loader type="chip"></v-skeleton-loader>
                <v-skeleton-loader type="chip"></v-skeleton-loader>
              </template>
            </div>
            <div class="metric-card-body pt-0">
              <apexchart v-if="ready" type="bar" :height="chartHeight" :options="optionsEvolucion" :series="seriesEvolucion"></apexchart>
              <div v-else class="metric-skeleton-chart" :style="{ height: chartHeight - 30 + 'px' }">
                <div v-for="(bar, i) in skeletonBars" :key="i" class="metric-skeleton-group">
                  <div class="metric-skeleton-bar" :style="{ height: bar[0] + '%' }"></div>
                  <div class="metric-skeleton-bar" :style="{ height: bar[1] + '%' }"></div>
                </div>
              </div>
            </div>
          </v-card>
        </div>
        <div class="col-md-4 d-flex flex-column metric-stack">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">Composición</p>
              <p class="caption text-slate-500 mb-0">Origen de los gastos y otros ingresos</p>
            </div>
            <div class="metric-card-body">
              <div v-for="linea in composicion" :key="linea.name" class="metric-line">
                <span class="metric-line-name">{{ linea.name }}</span>
                <span v-if="ready" class="metric-line-amount">{{ money(linea.amount) }}</span>
                <v-skeleton-loader v-else type="text" width="30%" class="metric-skeleton-text"></v-skeleton-loader>
              </div>
            </div>
          </v-card>
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">Facturación</p>
              <p class="caption text-slate-500 mb-0">Comprobantes de venta del período</p>
            </div>
            <div class="metric-card-body">
              <div v-for="linea in facturacion" :key="linea.name" class="metric-line">
                <span class="metric-line-name">{{ linea.name }}</span>
                <span v-if="ready" class="metric-line-amount">{{ linea.value }}</span>
                <v-skeleton-loader v-else type="text" width="30%" class="metric-skeleton-text"></v-skeleton-loader>
              </div>
            </div>
          </v-card>
        </div>
      </div>

      <!-- Rankings -->
      <div class="row" v-for="(fila, f) in rankings" :key="f">
        <div class="col-md-6" v-for="ranking in fila" :key="ranking.key">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">{{ ranking.title }}</p>
              <p class="caption text-slate-500 mb-0">{{ ranking.caption }}</p>
            </div>
            <div class="metric-card-body">
              <template v-if="ready">
                <p v-if="data[ranking.key].items.length === 0" class="caption mb-0">Sin movimientos en el período.</p>
                <div v-for="item in data[ranking.key].items" :key="String(item.id)" class="metric-share">
                  <div class="metric-line">
                    <span class="metric-line-name">{{ item.name }}</span>
                    <span class="metric-line-amount">
                      {{ money(item.amount) }}
                      <span class="caption ml-1" style="color: #757575">{{ formatPercent(item.percent) }}</span>
                    </span>
                  </div>
                  <div class="metric-share-bar">
                    <div :style="{ width: barWidth(item.percent), backgroundColor: item.id === 'otros' ? '#B0BEC5' : ranking.color }"></div>
                  </div>
                </div>
              </template>
              <div v-else v-for="n in 6" :key="n" class="metric-line">
                <v-skeleton-loader type="text" width="50%" class="metric-skeleton-text"></v-skeleton-loader>
                <v-skeleton-loader type="text" width="25%" class="metric-skeleton-text"></v-skeleton-loader>
              </div>
            </div>
          </v-card>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { metricsService } from "@/libs/ws/metricsService.js";
import { emmiterService } from "@/libs/ws/emmiterService";
import { formatUtils } from "@/libs/utils/formatUtils";

const COLOR_VENTAS = "#388E3C";
const COLOR_GASTOS = "#E53935";
const COLOR_DIRECTOS = "#EF9A9A";

/** Importe abreviado para los ejes: 1.500.000 -> "1,5 M". */
function shortMoney(val) {
  const abs = Math.abs(val);
  if (abs >= 1e9) return (val / 1e9).toFixed(1).replace(".", ",") + " MM";
  if (abs >= 1e6) return (val / 1e6).toFixed(1).replace(".", ",") + " M";
  if (abs >= 1e3) return (val / 1e3).toFixed(0) + " K";
  return Math.round(val).toString();
}

const MONTHS = [
  { id: 1, name: "Enero" },
  { id: 2, name: "Febrero" },
  { id: 3, name: "Marzo" },
  { id: 4, name: "Abril" },
  { id: 5, name: "Mayo" },
  { id: 6, name: "Junio" },
  { id: 7, name: "Julio" },
  { id: 8, name: "Agosto" },
  { id: 9, name: "Septiembre" },
  { id: 10, name: "Octubre" },
  { id: 11, name: "Noviembre" },
  { id: 12, name: "Diciembre" },
];

export default {
  data() {
    const year = new Date().getFullYear();
    return {
      COLOR_VENTAS: COLOR_VENTAS,
      COLOR_GASTOS: COLOR_GASTOS,
      // Arranca en true: el skeleton se ve desde que se entra, mientras se cargan las empresas.
      loading: true,
      emitters_selected: [],
      emitters_prev: [],
      emitters_error: false,
      loadSeq: 0,
      emitters: [],
      period_type: "month",
      months: MONTHS,
      mes_selected: MONTHS[new Date().getMonth()],
      year: year,
      years: Array.from({ length: 6 }, (_, i) => year - i),
      chartHeight: 280,
      // Alturas (%) de las barras ventas / gastos del skeleton del grafico.
      skeletonBars: [[55, 40], [70, 50], [45, 60], [80, 55], [60, 35], [75, 65], [50, 45], [85, 60], [65, 50], [40, 30], [70, 55], [60, 45]],
      rankings: [
        [
          { key: "ventas_por_cliente", title: "Ventas por cliente", caption: "Clientes con mayor facturación neta", color: COLOR_VENTAS },
          { key: "ventas_por_rubro", title: "Ventas por rubro", caption: "Rubro del producto facturado", color: COLOR_VENTAS },
        ],
        [
          { key: "gastos_por_concepto", title: "Gastos por concepto", caption: "Comprobantes y egresos directos por tesorería", color: COLOR_GASTOS },
          { key: "gastos_por_proveedor", title: "Gastos por proveedor", caption: "Proveedores con mayor gasto neto (solo comprobantes)", color: COLOR_GASTOS },
        ],
        [
          { key: "gastos_por_centro_costo", title: "Gastos por centro de costo", caption: "Comprobantes y egresos directos por tesorería", color: COLOR_GASTOS },
          { key: "ventas_por_tipo_comprobante", title: "Ventas por tipo de comprobante", caption: "Las notas de crédito restan", color: COLOR_VENTAS },
        ],
      ],
      data: null,
    };
  },
  mounted() {
    emmiterService
      .filter({ enabled: true })
      .then((response) => {
        if (response.status === false) {
          this.emitters_error = true;
          this.loading = false;
          this.$toast.error(response.msg || "No se pudieron cargar las empresas. Intentá nuevamente.", "Error");
          return;
        }
        this.emitters = response.result;
        // Por defecto se elige la primera empresa.
        if (this.emitters.length > 0) {
          this.emitters_selected = [this.emitters[0].id];
          this.emitters_prev = [...this.emitters_selected];
          this.load();
        } else {
          this.loading = false;
        }
      })
      .catch(() => {
        this.emitters_error = true;
        this.loading = false;
        this.$toast.error("No se pudieron cargar las empresas. Intentá nuevamente.", "Error");
      });
  },
  computed: {
    // Los titulos de las cards se muestran siempre; los valores solo cuando llegaron los datos.
    ready() {
      return !this.loading && this.data != null;
    },
    k() {
      return this.ready ? this.data.kpis : {};
    },
    kpis() {
      const k = this.k;
      return [
        { title: "Ventas netas", caption: "Facturado neto de notas de crédito", value: this.money(k.ventas) },
        { title: "Gastos totales", caption: "Comprobantes + directos por tesorería", value: this.money(k.gastos) },
        { title: "Resultado económico", caption: "Ventas + otros ingresos - gastos", value: this.money(k.resultado), color: k.resultado < 0 ? COLOR_GASTOS : COLOR_VENTAS },
        { title: "Margen", caption: "Resultado sobre ventas", value: k.margen == null ? "-" : this.formatPercent(k.margen), color: k.margen < 0 ? COLOR_GASTOS : COLOR_VENTAS },
      ];
    },
    composicion() {
      const k = this.k;
      return [
        { name: "Gastos por comprobantes", amount: k.gastos_cbte },
        { name: "Egresos directos por tesorería", amount: k.gastos_directos },
        { name: "Otros ingresos directos", amount: k.otros_ingresos },
      ];
    },
    facturacion() {
      const c = this.ready ? this.data.comprobantes : {};
      return [
        { name: "Comprobantes emitidos", value: c.cantidad },
        { name: "Notas de crédito", value: c.notas_credito },
        { name: "Ticket promedio", value: this.money(c.ticket_promedio) },
      ];
    },
    seriesEvolucion() {
      const e = this.data.evolucion;
      return [
        { name: "Ventas", group: "ventas", data: e.ventas },
        { name: "Gastos por comprobantes", group: "gastos", data: e.gastos_cbte },
        { name: "Egresos directos", group: "gastos", data: e.gastos_directos },
      ];
    },
    optionsEvolucion() {
      return {
        chart: { stacked: true, toolbar: { show: false }, zoom: { enabled: false } },
        dataLabels: { enabled: false },
        colors: [COLOR_VENTAS, COLOR_GASTOS, COLOR_DIRECTOS],
        legend: { horizontalAlign: "left" },
        xaxis: { categories: this.data.evolucion.columnas.map((c) => c.name) },
        yaxis: { labels: { formatter: shortMoney } },
        tooltip: { y: { formatter: this.money } },
      };
    },
  },
  methods: {
    money(value) {
      return formatUtils.formatPrice(value);
    },
    formatPercent(value) {
      return new Intl.NumberFormat("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value) + " %";
    },
    barWidth(percent) {
      return Math.max(0, Math.min(100, percent)) + "%";
    },
    onEmittersChange() {
      // Sin empresas no hay nada que consolidar: se repone la selección anterior.
      if (this.emitters_selected.length === 0) {
        this.emitters_selected = [...this.emitters_prev];
        return;
      }
      this.emitters_prev = [...this.emitters_selected];
      this.load();
    },
    load() {
      if (this.emitters_selected.length === 0) {
        this.loadSeq++;
        this.data = null;
        this.loading = false;
        return;
      }
      if (!this.year || (this.period_type === "month" && !(this.mes_selected && this.mes_selected.id))) {
        this.loadSeq++;
        this.loading = false;
        return;
      }

      const param = { emitters: this.emitters_selected, period_type: this.period_type };
      if (this.period_type === "month") param.month = this.year + "-" + String(this.mes_selected.id).padStart(2, "0");
      else param.year = this.year;

      // Solo la respuesta de la última carga pisa los datos y apaga el loading.
      const seq = ++this.loadSeq;
      this.loading = true;
      metricsService
        .getEstadoEconomico(param)
        .then((response) => {
          if (seq !== this.loadSeq) return;
          if (response.status === false) {
            this.$toast.error(response.msg, "Error");
            this.data = null;
            return;
          }
          this.data = response.result;
        })
        .catch((error) => {
          if (seq !== this.loadSeq) return;
          console.log(error);
          this.$toast.error("No se pudo cargar el estado económico. Intentá nuevamente.", "Error");
          this.data = null;
        })
        .finally(() => {
          if (seq === this.loadSeq) this.loading = false;
        });
    },
  },
};
</script>
